<template>
    <div>
        <step-form-heading v-if="!props.contactId" :icon="faUser">
            New Contact

            <template v-slot:description> Add a new contact to start messaging. </template>
        </step-form-heading>

        <contact-details :group-id="groupId" :contactId="props.contactId">
            <form-wrapper
                :sections="stickyNavSections"
                :hideNav="Boolean(props.contactId) || stickyNavSections.length == 1"
            >
                <vx-alert v-if="groupId && onCorporate()" color="info" :closeable="false" shadow>
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </template>

                    <div class="flex items-center justify-between">
                        <div>
                            Contact will be in <strong>{{ group?.name || groupId }}</strong>
                        </div>
                        <vx-button
                            :href="`/settings/teams/${teamId}/groups/${groupId}/contacts`"
                            color="info"
                            size="sm"
                        >
                            Back to Group
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon>
                        </vx-button>
                    </div>
                </vx-alert>
                <contact-form-details :contactId="props.contactId"></contact-form-details>
                <vx-expandable-card v-if="!managesRelationships && props.contactId">
                    <template v-slot:content>
                        <contact-view-relationships></contact-view-relationships>
                    </template>
                </vx-expandable-card>
                <contact-form-subscriptions v-if="managesRelationships"></contact-form-subscriptions>
                <contact-form-tags v-if="managesRelationships"></contact-form-tags>
                <contact-form-groups v-if="FEATURES.contact_groups && !groupId"></contact-form-groups>
                <contact-form-custom-attributes v-if="managesRelationships"></contact-form-custom-attributes>

                <vx-sticky-footer>
                    <vx-button href="/contacts" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="contact-save"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="processing"
                    >
                        {{ props.contactId ? 'Update' : 'Create' }} Contact
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </contact-details>
    </div>
</template>

<script setup>
import { VxAlert, VxStickyFooter, VxButton, VxExpandableCard } from '@voxie/frontend-components';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGrid2Plus, faSquareArrowUpLeft, faUser } from '@fortawesome/pro-solid-svg-icons';
import { inject, ref, computed } from 'vue';
import ContactDetails from '../ContactDetails.vue';
import ContactFormDetails from './ContactFormDetails.vue';
import ContactFormSubscriptions from './ContactFormSubscriptions.vue';
import ContactFormTags from './ContactFormTags.vue';
import ContactFormGroups from './ContactFormGroups.vue';
import ContactFormCustomAttributes from './ContactFormCustomAttributes.vue';
import ContactViewRelationships from '../view/ContactViewRelationships.vue';
import { useContactDetails } from '../useContactDetails';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useUrlSearchParams } from '@vueuse/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { groupsClient } from '~/services';
import FormWrapper from '~/components/general/FormWrapper.vue';

const teamId = Spark.state.currentTeam.id;
const FEATURES = inject('FEATURES');

const { onCorporate } = useCurrentGroup();

const queryParams = useUrlSearchParams();

const groupId = defineModel('groupId');

if (!groupId.value) {
    groupId.value = queryParams.group_id;
}

const group = ref();

if (groupId.value && onCorporate()) {
    groupsClient.getGroup({ teamId }, groupId.value).then((response) => {
        group.value = response.data;
    });
}

const props = defineProps({
    contactId: {
        type: Number,
        required: false,
    },
});

const {
    contact,
    unmount,

    onMountedContact,

    save,
    saveSubscriptions,
    syncGroups,
    syncCustomAttributes,
    syncTags,

    getStickyPhoneNumber,
    getSubscriptions,
    getGroups,
    getTags,
    getCustomAttributes,
} = useContactDetails();

onMountedContact(() => {
    if (!props.contactId) {
        return;
    }
    getStickyPhoneNumber();
    getSubscriptions();
    getGroups();
    getTags();
    getCustomAttributes();
});

const { submit, processing } = useFormSubmission({
    submit: async () => {
        await save();
        await Promise.all([saveSubscriptions(), syncGroups(), syncCustomAttributes(), syncTags()]);
    },
    preserveScroll: () => props.contactId,
    reset: () => {
        if (!props.contactId) {
            unmount({ loading: false });
        }
    },
    notification: () => ({
        message: `Contact ${props.contactId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: groupId.value && onCorporate() ? 'Back to Group' : 'Back to Contacts',
            href:
                groupId.value && onCorporate()
                    ? `/settings/teams/${teamId}/groups/${groupId.value}/contacts`
                    : '/contacts',
        },
    }),
});

const stickyNavSections = computed(() =>
    [
        { id: 'details', label: 'Details' },
        managesRelationships.value && { id: 'subscriptions', label: 'Sub Status' },
        managesRelationships.value && { id: 'tags', label: 'Tags' },
        FEATURES.contact_groups && !groupId.value ? { id: 'groups', label: 'Groups' } : null,
        managesRelationships.value && { id: 'custom-attributes', label: 'Custom Attr' },
    ].filter((s) => !!s)
);

const managesRelationships = computed(() => {
    if (groupId.value && !props.contactId) {
        return false;
    }

    if (groupId.value && props.contactId && !contact.value.is_preferred) {
        return false;
    }

    return true;
});
</script>
